import {
  KeyboardArrowDown,
  KeyboardArrowRight,
} from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  FormControl,
  FormLabel,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import get from "lodash/get";
import { useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQuery } from "react-query";
import Breadcrumb from "../../components/Breadcrumb";
import MainLayout from "../../components/MainLayout";
import ProtectedPermissionPage from "../../components/ProtectedPermissionPage";
import useNotification from "../../hooks/useNotification";
import group from "../../services/group";
import report from "../../services/report";
import queryDefault from "../../utils/queryDefault";

type MonthlyIncome = {
  month: number;
  month_name: string;
  total_income: number;
};

type BranchItem = {
  branch_id: number;
  branch_name: string;
  monthly_income: MonthlyIncome[];
  total_annual_income: number;
};

type BranchGroupItem = {
  branch_group_id: number;
  branch_group_name: string;
  monthly_income: MonthlyIncome[];
  total_annual_income: number;
  branches: BranchItem[];
};

type DashboardFiltersForm = {
  year: number | string;
  branch_groups: any[];
};

const MONTHS = [
  { month: 1, month_name: "January" },
  { month: 2, month_name: "February" },
  { month: 3, month_name: "March" },
  { month: 4, month_name: "April" },
  { month: 5, month_name: "May" },
  { month: 6, month_name: "June" },
  { month: 7, month_name: "July" },
  { month: 8, month_name: "August" },
  { month: 9, month_name: "September" },
  { month: 10, month_name: "October" },
  { month: 11, month_name: "November" },
  { month: 12, month_name: "December" },
];

const getErrorMessage = (error: any, fallback: string) =>
  error?.response?.data?.message || fallback;

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("id-ID", {
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const findMonthIncome = (monthlyIncome: MonthlyIncome[] = [], month: number) => {
  const item = monthlyIncome.find((entry) => Number(entry.month) === Number(month));
  return Number(item?.total_income || 0);
};

const normalizeParams = (filters: { year: number; branchGroupIds: number[] }) => {
  const params: any = {
    year: Number(filters.year),
  };

  if (filters.branchGroupIds.length > 0) {
    params.branch_group_ids = filters.branchGroupIds.join(",");
  }

  return params;
};

export default function ReportDashboard() {
  const notification = useNotification();
  const currentYear = new Date().getFullYear();
  const [expandedRows, setExpandedRows] = useState<Record<number, boolean>>({});
  const [downloadError, setDownloadError] = useState("");
  const [filters, setFilters] = useState<{ year: number; branchGroupIds: number[] }>({
    year: currentYear,
    branchGroupIds: [],
  });

  const form = useForm<DashboardFiltersForm>({
    defaultValues: {
      year: currentYear,
      branch_groups: [],
    },
  });

  const groups = useQuery(
    ["report-dashboard-groups"],
    () => {
      return group.getAllBranchGroup({ keyword: "" });
    },
    {
      ...queryDefault,
    }
  );

  const reportDashboard = useQuery(
    ["report-dashboard", filters],
    ({ queryKey }) => {
      return report.getReportDashboard(normalizeParams(queryKey[1] as any));
    },
    {
      ...queryDefault,
      onError: (error: any) => {
        notification.onOpen({
          message: getErrorMessage(error, "Failed to get report dashboard data"),
          type: "error",
          position: "top",
        });
      },
    }
  );

  const downloadDashboard = useMutation(
    "download-report-dashboard",
    report.downloadReportDashboard,
    {
      onSuccess: () => {
        setDownloadError("");
      },
      onError: (error: any) => {
        setDownloadError(
          getErrorMessage(error, "Failed to download report dashboard file")
        );
      },
      ...queryDefault,
    }
  );

  const dashboardData = get(reportDashboard, "data.data", {});
  const branchGroups: BranchGroupItem[] = get(dashboardData, "branch_groups", []);

  const summaryMonthly: MonthlyIncome[] = useMemo(() => {
    const monthly = get(dashboardData, "total_monthly_income", []);
    return MONTHS.map((monthItem) => {
      const matched = monthly.find(
        (item: MonthlyIncome) => Number(item.month) === Number(monthItem.month)
      );
      return {
        month: monthItem.month,
        month_name: monthItem.month_name,
        total_income: Number(matched?.total_income || 0),
      };
    });
  }, [dashboardData]);

  const onSubmitFilter = (values: DashboardFiltersForm) => {
    const selectedIds = (values.branch_groups || []).map((item: any) => Number(item.id));

    setDownloadError("");
    setFilters({
      year: Number(values.year),
      branchGroupIds: selectedIds,
    });
  };

  const onResetFilter = () => {
    form.reset({
      year: currentYear,
      branch_groups: [],
    });

    setExpandedRows({});
    setDownloadError("");
    setFilters({
      year: currentYear,
      branchGroupIds: [],
    });
  };

  const toggleRow = (branchGroupId: number) => {
    setExpandedRows((prev) => ({
      ...prev,
      [branchGroupId]: !prev[branchGroupId],
    }));
  };

  const onDownload = () => {
    downloadDashboard.mutate(normalizeParams(filters));
  };

  const onRetryDownload = () => {
    onDownload();
  };

  return (
    <MainLayout>
      <Helmet>
        <title>REPORT DASHBOARD</title>
      </Helmet>

      <Breadcrumb
        label="Report Dashboard"
        breadcrumbs={[
          { label: "Report", href: "/app/report" },
          { label: "Report Dashboard", href: "/app/report/dashboard" },
        ]}
      />

      <ProtectedPermissionPage acceptPermissions={[1, 2, 3]}>
        <Box sx={{ mt: 3 }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", md: "end" }}
          >
            <FormControl sx={{ width: { xs: "100%", md: 220 } }}>
              <FormLabel required>Year</FormLabel>
              <Controller
                name="year"
                control={form.control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="number"
                    placeholder="YYYY"
                    inputProps={{ min: 1000, max: 9999 }}
                  />
                )}
              />
            </FormControl>

            <FormControl sx={{ width: { xs: "100%", md: 420 } }}>
              <FormLabel>Branch Group</FormLabel>
              <Controller
                name="branch_groups"
                control={form.control}
                render={({ field }) => (
                  <Autocomplete
                    multiple
                    options={get(groups, "data.data", [])}
                    value={field.value || []}
                    getOptionLabel={(option: any) => String(option.name || "")}
                    isOptionEqualToValue={(option: any, value: any) =>
                      Number(option.id) === Number(value.id)
                    }
                    onChange={(_, newValue) => {
                      field.onChange(newValue);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select branch group(s)"
                      />
                    )}
                  />
                )}
              />
            </FormControl>

            <Stack direction="row" spacing={1}>
              <LoadingButton
                variant="contained"
                onClick={form.handleSubmit(onSubmitFilter)}
                loading={reportDashboard.isFetching}
              >
                Apply
              </LoadingButton>

              <Button variant="outlined" onClick={onResetFilter}>
                Reset
              </Button>

              <LoadingButton
                variant="contained"
                color="success"
                onClick={onDownload}
                loading={downloadDashboard.isLoading}
              >
                Download Excel
              </LoadingButton>
            </Stack>
          </Stack>
        </Box>

        {downloadError ? (
          <Alert
            severity="error"
            sx={{ mt: 2 }}
            action={
              <Button color="inherit" size="small" onClick={onRetryDownload}>
                Retry
              </Button>
            }
          >
            {downloadError}
          </Alert>
        ) : null}

        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
            Summary
          </Typography>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2} flexWrap="wrap">
              {summaryMonthly.map((item) => (
                <Box
                  key={item.month}
                  sx={{
                    minWidth: 150,
                    border: "1px solid #e3e3e3",
                    borderRadius: 1,
                    px: 1.5,
                    py: 1,
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    {item.month_name}
                  </Typography>
                  <Typography variant="body2" fontWeight={700}>
                    Rp {formatCurrency(item.total_income)}
                  </Typography>
                </Box>
              ))}
            </Stack>

            <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 2 }}>
              Annual Total: Rp {formatCurrency(Number(get(dashboardData, "total_annual_income", 0)))}
            </Typography>
          </Paper>
        </Box>

        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
            Branch Group Detail
          </Typography>

          <TableContainer component={Paper} variant="outlined" sx={{ overflowX: "auto" }}>
            <Table size="small" sx={{ minWidth: 1300 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 56 }} />
                  <TableCell sx={{ minWidth: 220, fontWeight: 700 }}>Name</TableCell>
                  {MONTHS.map((item) => (
                    <TableCell key={item.month} align="right" sx={{ fontWeight: 700 }}>
                      {item.month_name}
                    </TableCell>
                  ))}
                  <TableCell align="right" sx={{ fontWeight: 700, minWidth: 130 }}>
                    Annual Total
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {branchGroups.map((branchGroup) => {
                  const expanded = Boolean(expandedRows[branchGroup.branch_group_id]);

                  return (
                    <>
                      <TableRow
                        key={`group-${branchGroup.branch_group_id}`}
                        sx={{
                          backgroundColor: "#f8fafc",
                        }}
                      >
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => toggleRow(branchGroup.branch_group_id)}
                          >
                            {expanded ? <KeyboardArrowDown /> : <KeyboardArrowRight />}
                          </IconButton>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          {branchGroup.branch_group_name}
                        </TableCell>
                        {MONTHS.map((monthItem) => (
                          <TableCell key={monthItem.month} align="right" sx={{ fontWeight: 700 }}>
                            Rp {formatCurrency(findMonthIncome(branchGroup.monthly_income, monthItem.month))}
                          </TableCell>
                        ))}
                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                          Rp {formatCurrency(branchGroup.total_annual_income)}
                        </TableCell>
                      </TableRow>

                      {expanded &&
                        (branchGroup.branches || []).map((branchItem) => (
                          <TableRow key={`branch-${branchItem.branch_id}`}>
                            <TableCell />
                            <TableCell sx={{ pl: 5 }}>{branchItem.branch_name}</TableCell>
                            {MONTHS.map((monthItem) => (
                              <TableCell key={monthItem.month} align="right">
                                Rp {formatCurrency(findMonthIncome(branchItem.monthly_income, monthItem.month))}
                              </TableCell>
                            ))}
                            <TableCell align="right">
                              Rp {formatCurrency(branchItem.total_annual_income)}
                            </TableCell>
                          </TableRow>
                        ))}
                    </>
                  );
                })}

                {!reportDashboard.isLoading && branchGroups.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={15} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        No data found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </ProtectedPermissionPage>
    </MainLayout>
  );
}
